import { useQuery } from '@tanstack/react-query';
import { formatDateForCheckingState } from '../../../../../utilities/dateFormatters';
import { AxiosInstance } from '../../../../../utilities/requestClient';

/**
 * One report-delivery timeline row from the API:
 * reference date, three pipeline timestamps (often empty when not yet run), status label.
 */
export type MancoReportDeliveryRow = readonly [
  referenceDate: string,
  timestamp1: string,
  timestamp2: string,
  timestamp3: string,
  status: string,
];

/** Fund id → rows for that fund (may be an empty array). */
export type MancoReportDeliveryFundRows = Record<
  string,
  MancoReportDeliveryRow[]
>;

export type MancoReportDeliveryWeekSummary = {
  week_start_date: string;
  week_end_date: string;
  no_data_reports: number;
  total_reports: number;
  complete_reports: number;
  pending_reports: number;
  analyst_input_reports: number;
  processing_reports: number;
};

export type MancoReportFundInformation = {
  fund_name: string;
  frequency_type: 'daily' | 'weekly' | 'monthly' | 'fortnightly';
};

/** Payload from `getMancoReportDeliveryStatus` / `getMancoReportDeliveryStatus/:date`. */
export type MancoReportDeliveryStatus = {
  processing_funds: MancoReportDeliveryFundRows;
  analyst_input_required: MancoReportDeliveryFundRows;
  funds: MancoReportDeliveryFundRows;
  complete_funds: MancoReportDeliveryFundRows;
  manco: MancoReportDeliveryWeekSummary;
  no_data_funds: MancoReportDeliveryFundRows;
  pending_funds: MancoReportDeliveryFundRows;
  fund_information: Record<string, MancoReportFundInformation>;
};

function buildMancoReportDeliveryStatusUrl(weekStartDate: Date): string {
  const today = new Date();
  const isToday =
    formatDateForCheckingState(weekStartDate) ===
    formatDateForCheckingState(today);

  return isToday
    ? 'getMancoReportDeliveryStatus'
    : `getMancoReportDeliveryStatus/${formatDateForCheckingState(weekStartDate)}`;
}

async function fetchMancoReportDeliveryStatus(
  weekStartDate: Date,
): Promise<MancoReportDeliveryStatus> {
  const url = buildMancoReportDeliveryStatusUrl(weekStartDate);
  const response = await AxiosInstance.get<MancoReportDeliveryStatus>(url);
  return response.data;
}

export function useMancoReportDeliveryStatus(
  weekStartDate: Date,
): ReturnType<typeof useQuery<MancoReportDeliveryStatus, unknown>> {
  const url = buildMancoReportDeliveryStatusUrl(weekStartDate);

  return useQuery({
    queryKey: ['manco-report-delivery-status', url],
    queryFn: () => fetchMancoReportDeliveryStatus(weekStartDate),
    // Poll every 10 seconds and keep previous data during refetch
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });
}
